import React, { useState, useEffect, useCallback } from 'react';
import { debounce } from 'lodash';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  setNotes as setNotesInStore,
  setTitle as setTitleInStore,
} from '../recorderSlice';
import { TimeStampedLine } from '../../types';

const formatTime = (seconds: number): string => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `[${h.toString().padStart(2, '0')}:${m
    .toString()
    .padStart(2, '0')}:${s.toString().padStart(2, '0')}]`;
};

const Notepad = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [title, setTitle] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editableTitle, setEditableTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<TimeStampedLine[]>([]);
  const [recordingStartTime] = useState(Date.now());

  const debouncedSetNotes = useCallback(
    debounce((notes0: TimeStampedLine[]) => {
      dispatch(setNotesInStore(notes0));
    }, 1000),
    [dispatch],
  );

  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    let newValue = e.target.value;

    // Auto-format bullet points
    newValue = newValue.replace(/^- /gm, '• '); // Convert "- " to "• " at start of lines
    // Convert "[] " or "[ ] " to "☐ "
    newValue = newValue.replace(/^\[[ ]?\] /gm, '☐ ');
    // Convert "[x] " or "[X] " to "☑ "
    newValue = newValue.replace(/^\[[xX]\] /gm, '☑ ');
    // Convert "!" at start of line to attention marker
    newValue = newValue.replace(/^! /gm, '⚠️ ');
    // Convert arrows
    newValue = newValue.replace(/->/g, '→');
    newValue = newValue.replace(/<-/g, '←');

    setNotes(newValue);

    // Split into lines and update timestamps
    const currentLines = newValue.split('\n');
    const updatedLines = currentLines.map((text, index) => {
      const existingLine = lines[index];
      if (existingLine && existingLine.text === text) {
        return existingLine;
      }
      return {
        text,
        timestamp: Math.floor((Date.now() - recordingStartTime) / 1000),
      };
    });

    setLines(updatedLines);
    debouncedSetNotes(updatedLines);

    // Log the timestamped version
    const timestampedText = updatedLines
      .map((line) => `${formatTime(line.timestamp)} ${line.text}`)
      .join('\n');
    console.log('Timestamped Notes:', timestampedText);
  };

  useEffect(() => {
    const now = new Date();
    const formattedDate = now.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    const defaultTitle = t('recorder.notepad.title', {
      defaultValue: `New Recording ${formattedDate}`,
      date: formattedDate,
    });
    setTitle(defaultTitle);
    setEditableTitle(defaultTitle);
  }, []);

  const handleTitleClick = () => {
    setIsEditing(true);
  };

  const handleTitleBlur = () => {
    setIsEditing(false);
    if (editableTitle.trim() === '') {
      const now = new Date();
      const formattedDate = now.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      const defaultTitle = t('recorder.notepad.title', {
        defaultValue: `New Recording ${formattedDate}`,
        date: formattedDate,
      });
      setTitle(defaultTitle);
      setEditableTitle(defaultTitle);
      dispatch(setTitleInStore(defaultTitle));
    } else {
      setTitle(editableTitle);
      dispatch(setTitleInStore(editableTitle));
    }
  };

  const handleTitleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    event.target.select();
  };

  return (
    <div className="h-full w-full max-w-[600px] flex flex-col gap-2">
      <div className="flex items-center gap-2">
        {isEditing ? (
          <Input
            value={editableTitle}
            onChange={(e) => setEditableTitle(e.target.value)}
            onBlur={handleTitleBlur}
            onFocus={handleTitleFocus}
            autoFocus
            className="flex-1 text-sm font-medium h-9"
          />
        ) : (
          <button
            type="button"
            onClick={handleTitleClick}
            className="flex-1 text-sm font-medium px-3 h-9 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground text-left transition-colors"
            aria-label="Edit title"
          >
            {title}
          </button>
        )}
      </div>
      <div className="flex-1 flex flex-col min-h-0 rounded-md border border-input bg-background">
        <Textarea
          value={notes}
          onChange={handleNotesChange}
          placeholder={t('recorder.notepad.placeholder', 'Write notes...')}
          className="flex-1 resize-none min-h-[200px] border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
        />
      </div>
    </div>
  );
};

export default Notepad;
